import { useMemo } from "react";

import type { FilterConfig, FilterFieldDef } from "#app/lib/filters/types";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import { buildCustomFieldFilterDef } from "#app/lib/filters/custom-field-filter-fields";
import { useAllCustomFields } from "#app/lib/hooks/use-all-custom-fields";

/**
 * The issue filter config with every workspace custom field appended as an
 * advanced-filter field (keyed `values.<customFieldId>`). Custom fields are
 * workspace-scoped — there is no per-workflow field attachment server-side —
 * so every issue surface offers the same set, sorted by name. The filter
 * row's field picker renders these as a separate "Custom fields" group
 * (partitioned on `customField`).
 */
export function useIssueFilterConfig(): FilterConfig {
  const customFields = useAllCustomFields();

  return useMemo(() => {
    if (!customFields.length) return issueFilterConfig;
    const fields: Record<string, FilterFieldDef> = { ...issueFilterConfig.fields };
    const sorted = [...customFields].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    for (const cf of sorted) {
      const def = buildCustomFieldFilterDef(cf);
      if (def) fields[def.field] = def;
    }
    return { ...issueFilterConfig, fields };
  }, [customFields]);
}
